import React, { FormEvent, useState } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { InvalidCredentialsError } from '../../api/auth.api';
import Banner from '../../components/general/Banner';
import Logo from '../../components/general/Logo';
import { ArrowRightIcon, EyeIcon, EyeOffIcon, LockIcon } from '../../components/icons/Icons';
import { useTranslation } from '../../i18n/i18n';
import { weatherListUrl } from '../../routes.constants';
import useAuthStore from '../../stores/auth.store';
import './SignInPage.scss';

const SignInPage = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'signin' });
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<'invalid' | 'unavailable'>();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      await login(email.trim(), password, remember);
      const from = (location.state as { from?: Location } | null)?.from;
      navigate(from ? `${from.pathname}${from.search}` : weatherListUrl, { replace: true });
    } catch (err) {
      setError(err instanceof InvalidCredentialsError ? 'invalid' : 'unavailable');
      setSubmitting(false);
    }
  };

  return (
    <div className="SignInPage">
      <section className="SignInPage__inner">
        <Logo size={34} textSize={24} />
        <form className="SignInPage__card" onSubmit={onSubmit} noValidate>
          <div className="SignInPage__heading">
            <h2>{t('title')}</h2>
            <p>{t('subtitle')}</p>
          </div>

          {error && <Banner kind="error">{t(error)}</Banner>}

          <div className="SignInPage__field">
            <label htmlFor="tp-user">{t('email')}</label>
            <input
              id="tp-user"
              type="email"
              autoComplete="username"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="SignInPage__field">
            <label htmlFor="tp-pass">{t('password')}</label>
            <div className="SignInPage__password">
              <input
                id="tp-pass"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                aria-label={t(showPassword ? 'hidePassword' : 'showPassword')}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="SignInPage__options">
            <label>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              {t('remember')}
            </label>
          </div>

          <button type="submit" className="SignInPage__submit" disabled={submitting || !email || !password}>
            {t(submitting ? 'submitting' : 'submit')}
            <ArrowRightIcon />
          </button>

          <p className="SignInPage__footer">
            <LockIcon />
            {t('secure')}
          </p>
        </form>
      </section>
    </div>
  );
};

export default SignInPage;
